import { useInfiniteQuery, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  createBookkeepingAccount,
  deleteBookkeepingAccount,
  fetchAllBookkeepingAccounts,
  fetchBookkeepingAccounts,
  updateBookkeepingAccount,
} from '~/api/bookkeeping/bookkeeping-accounts';
import { queryClient } from '~/client/queryClient';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import {
  BookkeepingAccount,
  BookkeepingAccountFilters,
  BookkeepingAccountQueryKeys,
  CreateBookkeepingAccount,
  UpdateBookkeepingAccount,
} from '~/types/general-bookkeeping';

export const useAllBookkeepingAccountsSuspenseQuery = (filters: BookkeepingAccountFilters) => {
  const companyId = useActiveCompanyId();

  return useSuspenseQuery({
    queryKey: BookkeepingAccountQueryKeys.list(companyId, filters),
    queryFn: () => fetchAllBookkeepingAccounts(companyId, filters),
    staleTime: Infinity,
    select: (accounts) => {
      const accountsMap: Record<string, BookkeepingAccount> = {};
      accounts.forEach((account) => {
        accountsMap[account.id] = account;
      });
      return accountsMap;
    },
  });
};

export const useBookkeepingAccountsInfiniteQuery = (filters: BookkeepingAccountFilters = {}) => {
  const companyId = useActiveCompanyId();

  return useInfiniteQuery({
    queryKey: BookkeepingAccountQueryKeys.infiniteList(companyId, filters),
    queryFn: ({ pageParam }) =>
      fetchBookkeepingAccounts(companyId, filters, {
        cursor: pageParam || undefined,
        include: ['parent'],
      }),
    getNextPageParam: ({ page }) => page?.cursor ?? undefined,
    initialPageParam: '',
    staleTime: Infinity,
    select: (data) => data.pages.flatMap((page) => page.accounts),
  });
};

export const useAllBookkeepingAccountsQuery = (filters: BookkeepingAccountFilters = {}) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: BookkeepingAccountQueryKeys.list(companyId, filters),
    queryFn: () => fetchAllBookkeepingAccounts(companyId, filters),
    staleTime: Infinity,
  });
};

export const useBookkeepingAccountsByCodes = (codes: (string | null)[]) => {
  const companyId = useActiveCompanyId();
  return useSuspenseQuery({
    queryKey: BookkeepingAccountQueryKeys.byCodes(companyId, codes),
    queryFn: () => fetchBookkeepingAccounts(companyId, { codes }),
    staleTime: Infinity,
  });
};

export const useBookkeepingAccountIsUsedQuery = (account: BookkeepingAccount) => {
  const companyId = useActiveCompanyId();
  const filters: BookkeepingAccountFilters = { code: account.code, used: true };
  return useQuery({
    queryKey: BookkeepingAccountQueryKeys.list(companyId, filters),
    queryFn: () => fetchBookkeepingAccounts(companyId, filters, { size: 1 }),
    select: (data) => data.accounts.length > 0,
  });
};

const invalidateBookkeepingAccountsCache = () => {
  queryClient.invalidateQueries({ queryKey: BookkeepingAccountQueryKeys.all });
};

export const useCreateBookkeepingAccountMutation = () => {
  const companyId = useActiveCompanyId();
  const { captureException } = useExceptionLogger();
  return useMutation({
    mutationFn: (values: Omit<CreateBookkeepingAccount, 'companyId'>) =>
      createBookkeepingAccount({ ...values, companyId }),
    onSuccess: invalidateBookkeepingAccountsCache,
    onError: (error, values) => captureException(error, { extra: { values } }),
  });
};

export const useUpdateBookkeepingAccountMutation = () => {
  const { captureException } = useExceptionLogger();
  return useMutation({
    mutationFn: (values: UpdateBookkeepingAccount) => updateBookkeepingAccount(values),
    onSuccess: invalidateBookkeepingAccountsCache,
    onError: (error, values) => captureException(error, { extra: { values } }),
  });
};

export const useDeleteBookkeepingAccountMutation = () => {
  const { captureException } = useExceptionLogger();
  return useMutation({
    mutationFn: (id: BookkeepingAccount['id']) => deleteBookkeepingAccount(id),
    onSuccess: invalidateBookkeepingAccountsCache,
    onError: (error, id) => captureException(error, { extra: { id } }),
  });
};
